/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { formatEventTimeDisplay } from '../utils/eventTime';
import {
  formatEventStatusLabel,
  getEventStatus,
  parsePublicEventDate,
  shouldShowOnHomepage,
  sortHomepageEvents
} from '../utils/eventUtils';
import { fetchPublicTestimonials } from '../api/testimonialApi';
import axios from 'axios';
import { deduplicateRequest } from '../utils/requestDeduplication';
import logoImage from '../assets/FINAL LOGO AK.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Aurora from '../components/Aurora';
import CookieConsent from '../components/CookieConsent';
import LogoLoop from '../components/LogoLoop';
import { 
  faQuoteLeft,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faUserTie,
  faLightbulb,
  faChalkboardTeacher,
  faChartLine,
  faHeart,
  faGraduationCap,
  faUsers,
  faUniversity,
  faBuilding,
  faHandsHelping,
  faUserPlus,
  faFileAlt,
  faCheckCircle,
  faRocket,
  faBriefcase,
  faChartBar,
  faHandshake,
  faArrowRight,
  faClock,
  faBell,
  faUserCheck,
  faMapMarkerAlt,
  faEye,
  faClipboardCheck,
  faTrophy,
  faCity,
  faSuitcase,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  faLinkedin, 
  faTwitter, 
  faFacebook, 
  faInstagram,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons';
import '../styles/HomePage.css';
import { normalizeIndustry } from '../utils/industryUtils';

const HERO_HEADLINE_VARIANTS = [
  'Find Your Dream Job',
  'Land Your Next Big Opportunity',
  'Build the Career You Deserve',
  'Unlock Your Future Role',
  'Step Into Your Next Success'
];
const DEFAULT_POPULAR_SEARCHES = [
  'Software Engineer',
  'Data Analyst',
  'Marketing Manager',
  'Sales Executive',
  'Accountant',
  'Project Manager',
  'Customer Support',
  'Graphic Designer',
  'HR Officer',
  'Business Analyst',
  'Operations Manager',
  'Frontend Developer'
];
const DEFAULT_KENYA_TRENDING_LOCATIONS = [
  'Nairobi',
  'Mombasa',
  'Kisumu',
  'Nakuru',
  'Eldoret',
  'Thika',
  'Kiambu',
  'Machakos',
  'Naivasha',
  'Nyeri',
  'Kakamega',
  'Meru'
];

const resolveTestimonialUploadUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalized = path.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) {
    const relativePath = normalized.replace(/^uploads\//, '');
    return buildApiUrl(`/api/users/uploads/${relativePath}`);
  }
  return buildApiUrl(`/${normalized}`);
};

const mapTestimonialPayload = (testimonial) => {
  if (!testimonial) return null;
  const combinedRole = [testimonial.headline, testimonial.company].filter(Boolean).join(' • ');
  return {
    id: testimonial.id || testimonial._id,
    quote: testimonial.quote || testimonial.message || '',
    author: testimonial.name || testimonial.author || 'AksharJobs User',
    role: combinedRole || testimonial.role || '',
    photoUrl: resolveTestimonialUploadUrl(testimonial.photoPath || testimonial.photoUrl)
  };
};

const HomePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [featuredInternships, setFeaturedInternships] = useState([]);
  const [loadingInternships, setLoadingInternships] = useState(true);
  const [activeFeaturedTab, setActiveFeaturedTab] = useState('jobs');
  const [jobSuggestions, setJobSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [platformStats, setPlatformStats] = useState({
    activeJobs: null,
    companies: null,
    jobSeekers: null,
    successfulHires: null,
    countriesRepresented: null,
    lastUpdated: null
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [activeHowItWorksTab, setActiveHowItWorksTab] = useState('jobseekers');
  const [animatedStats, setAnimatedStats] = useState({
    activeJobs: 0,
    companies: 0,
    jobSeekers: 0,
    successfulHires: 0,
    countriesRepresented: 0
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsSectionRef = React.useRef(null);
  const searchTimeoutRef = React.useRef(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [trendingLocations, setTrendingLocations] = useState([]);
  const [trendingCountry, setTrendingCountry] = useState('Kenya');
  const [categoryCounts, setCategoryCounts] = useState({
    'Technology & IT': 0,
    'Healthcare': 0,
    'Finance & Accounting': 0,
    'Design & Creative': 0,
    'Marketing & Sales': 0,
    'Education': 0,
    'Engineering & Manufacturing': 0,
    'Legal Services': 0,
    'Hospitality & Tourism': 0,
    'Consumer Goods': 0,
    'Logistics & Supply Chain': 0,
    'Retail & E-commerce': 0,
    'Human Resources': 0,
    'Consulting & Professional Services': 0,
    'Construction & Real Estate': 0
  });
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [homeEvents, setHomeEvents] = useState([]);
  const [homeEventsSlideIndex, setHomeEventsSlideIndex] = useState(0);
  const [loadingUpcomingEvent, setLoadingUpcomingEvent] = useState(true);
  const [activeHeroHeadline, setActiveHeroHeadline] = useState(0);
  
  // Debug: Log when categoryCounts changes
  useEffect(() => {
    console.log('🔄 categoryCounts state changed:', categoryCounts);
  }, [categoryCounts]);

  useEffect(() => {
    const headlineRotateTimer = setInterval(() => {
      setActiveHeroHeadline((currentIndex) => (currentIndex + 1) % HERO_HEADLINE_VARIANTS.length);
    }, 3200);

    return () => clearInterval(headlineRotateTimer);
  }, []);

  const statCardConfig = [
    { key: 'activeJobs', label: 'Active Jobs', addPlus: true },
    { key: 'companies', label: 'Employers', addPlus: true },
    { key: 'jobSeekers', label: 'Job Seekers', addPlus: true },
    { key: 'successfulHires', label: 'Successful Hires', addPlus: true, value: 15 },
    { key: 'countriesRepresented', label: 'Countries', addPlus: false }
  ];

  const formatStatValue = (value, addPlus = true) => {
    if (value === null || value === undefined) {
      return statsLoading ? '—' : '0';
    }

    const numericValue = Number(value) || 0;

    if (numericValue >= 1_000_000) {
      const formatted = (numericValue / 1_000_000).toFixed(numericValue >= 10_000_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}M${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    if (numericValue >= 1_000) {
      const formatted = (numericValue / 1_000).toFixed(numericValue >= 10_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}K${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    return `${numericValue.toLocaleString()}${addPlus && numericValue > 0 ? '+' : ''}`;
  };

  // Animate statistics counting up
  const animateStat = (startValue, endValue, duration, callback) => {
    if (startValue === endValue) {
      callback(endValue);
      return;
    }

    const startTime = performance.now();
    const difference = endValue - startValue;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + difference * easeOut);
      
      callback(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        callback(endValue);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    const fetchPlatformStats = async () => {
      try {
        setStatsLoading(true);
        const response = await deduplicateRequest(
          'stats_summary',
          () => fetch(buildApiUrl('/api/public/stats-summary'))
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        const payload = await response.json();
        if (payload?.success && payload.data) {
          const data = payload.data;
          setPlatformStats({
            activeJobs: data.activeJobs ?? 0,
            companies: data.companies ?? 0,
            jobSeekers: data.jobSeekers ?? 0,
            successfulHires: data.successfulHires ?? 0,
            countriesRepresented: data.countriesRepresented ?? 0,
            lastUpdated: payload.meta?.lastUpdated || new Date().toISOString()
          });
        } else {
          console.warn('Stats summary did not return success flag.', payload);
          setPlatformStats(prev => ({
            ...prev,
            lastUpdated: new Date().toISOString()
          }));
        }
      } catch (error) {
        console.error('Error fetching platform stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchPlatformStats();
  }, []);

  // Intersection Observer for scroll-triggered animation
  useEffect(() => {
    if (hasAnimated || statsLoading || !statsSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Animate each stat with a slight delay for staggered effect
            statCardConfig.forEach((card, index) => {
              const targetValue = card.value ?? platformStats[card.key] ?? 0;
              const numericValue = Number(targetValue) || 0;
              
              setTimeout(() => {
                animateStat(0, numericValue, 2000, (currentValue) => {
                  setAnimatedStats((prev) => ({
                    ...prev,
                    [card.key]: currentValue
                  }));
                });
              }, index * 150); // 150ms delay between each stat
            });
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: '0px'
      }
    );

    const currentRef = statsSectionRef.current;
    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated, statsLoading, platformStats.activeJobs, platformStats.companies, platformStats.jobSeekers, platformStats.successfulHires, platformStats.countriesRepresented]);

  // Reset animated stats when platform stats change
  useEffect(() => {
    if (!statsLoading && platformStats) {
      setAnimatedStats({
        activeJobs: 0,
        companies: 0,
        jobSeekers: 0,
        successfulHires: 0,
        countriesRepresented: 0
      });
      setHasAnimated(false);
    }
  }, [platformStats.activeJobs, platformStats.companies, platformStats.jobSeekers, platformStats.successfulHires, platformStats.countriesRepresented]);

  // Fetch real recent activity feed
  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        const now = new Date();
        const activities = [];

        // Fetch stats once for all activities - with request deduplication
        let statsData = null;
        try {
          const statsResponse = await deduplicateRequest(
            'stats_summary',
            () => fetch(buildApiUrl('/api/public/stats-summary'))
          );
          if (statsResponse.ok) {
            const data = await statsResponse.json();
            if (data?.success && data.data) {
              statsData = data.data;
            }
          }
        } catch (error) {
          console.error('Error fetching stats:', error);
        }

        // Fetch recent jobs (last 24 hours) - with request deduplication
        try {
          const jobsResponse = await deduplicateRequest(
            'homepage_jobs_50',
            () => axios.get(buildApiUrl('/api/jobs/get_jobs?limit=50'))
          );
          const jobs = Array.isArray(jobsResponse.data) ? jobsResponse.data : [];
          
          // Filter jobs from last 24 hours
          const recentJobs = jobs.filter(job => {
            const postedDate = job.postedAt ? new Date(job.postedAt) : (job.created_at ? new Date(job.created_at) : null);
            if (!postedDate || isNaN(postedDate.getTime())) return false;
            const hoursAgo = (now - postedDate) / (1000 * 60 * 60);
            return hoursAgo <= 24;
          });

          if (recentJobs.length > 0) {
            const mostRecentJob = recentJobs[0];
            const postedDate = mostRecentJob.postedAt ? new Date(mostRecentJob.postedAt) : new Date(mostRecentJob.created_at);
            const minutesAgo = Math.floor((now - postedDate) / (1000 * 60));
            const hoursAgo = Math.floor((now - postedDate) / (1000 * 60 * 60));
            
            let timeText = '';
            if (minutesAgo < 1) {
              timeText = 'just now';
            } else if (minutesAgo < 60) {
              timeText = minutesAgo === 1 ? '1 minute ago' : `${minutesAgo} minutes ago`;
            } else if (hoursAgo < 24) {
              timeText = hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
            } else {
              timeText = 'today';
            }

            activities.push({
              type: 'job_posted',
              message: 'new jobs posted',
              count: recentJobs.length,
              time: timeText,
              icon: faSuitcase,
              color: '#f97316',
              timestamp: postedDate.toISOString()
            });
          }
        } catch (error) {
          console.error('Error fetching recent jobs:', error);
        }

        // Fetch real applications count (last 24 hours)
        try {
          // Try to get applications from API if available
          let applicationsResponse = null;
          try {
            applicationsResponse = await deduplicateRequest(
              'applications_all_1000',
              () => axios.get(buildApiUrl('/api/applications?limit=1000'), {
                headers: { 'Content-Type': 'application/json' }
              })
            );
          } catch (apiError) {
            // Silently handle missing or broken applications endpoint
            // This is optional data, so we'll just skip it
            applicationsResponse = null;
          }
          
          let recentApplicationsCount = 0;
          let mostRecentApplicationTime = null;
          
          if (applicationsResponse && Array.isArray(applicationsResponse.data)) {
            const applications = applicationsResponse.data;
            
            // Filter applications from last 24 hours
            const recentApplications = applications.filter(app => {
              const appliedDate = app.appliedAt ? new Date(app.appliedAt) : 
                                 (app.createdAt ? new Date(app.createdAt) : 
                                 (app.created_at ? new Date(app.created_at) : null));
              if (!appliedDate || isNaN(appliedDate.getTime())) return false;
              const hoursAgo = (now - appliedDate) / (1000 * 60 * 60);
              return hoursAgo <= 24;
            });
            
            recentApplicationsCount = recentApplications.length;
            
            if (recentApplications.length > 0) {
              // Get most recent application
              const mostRecent = recentApplications.sort((a, b) => {
                const dateA = a.appliedAt ? new Date(a.appliedAt) : (a.createdAt ? new Date(a.createdAt) : new Date(a.created_at || 0));
                const dateB = b.appliedAt ? new Date(b.appliedAt) : (b.createdAt ? new Date(b.createdAt) : new Date(b.created_at || 0));
                return dateB - dateA;
              })[0];
              
              mostRecentApplicationTime = mostRecent.appliedAt || mostRecent.createdAt || mostRecent.created_at;
            }
          } else if (statsData) {
            // Fallback: Use total applications if we can't get recent ones
            // Try to estimate from stats - assume some percentage of job seekers have applied
            const totalApplications = statsData.totalApplications || 
                                     (statsData.jobSeekers ? Math.floor(statsData.jobSeekers * 0.5) : 0);
            recentApplicationsCount = totalApplications > 0 ? Math.floor(totalApplications * 0.1) : 0; // Assume 10% are recent
          }

          if (recentApplicationsCount > 0) {
            let timeText = 'today';
            if (mostRecentApplicationTime) {
              const appliedDate = new Date(mostRecentApplicationTime);
              const minutesAgo = Math.floor((now - appliedDate) / (1000 * 60));
              const hoursAgo = Math.floor((now - appliedDate) / (1000 * 60 * 60));
              
              if (minutesAgo < 1) {
                timeText = 'just now';
              } else if (minutesAgo < 60) {
                timeText = minutesAgo === 1 ? '1 minute ago' : `${minutesAgo} minutes ago`;
              } else if (hoursAgo < 24) {
                timeText = hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
              }
            }

            activities.push({
              type: 'application',
              message: 'applications submitted',
              count: recentApplicationsCount,
              time: timeText,
              icon: faClipboardCheck,
              color: '#f97316',
              timestamp: mostRecentApplicationTime || new Date().toISOString()
            });
          }
        } catch (error) {
          console.error('Error fetching applications:', error);
        }

        // Add company joined activity (real data from stats)
        if (statsData && statsData.companies > 0) {
          activities.push({
            type: 'company_joined',
            message: 'employers joined',
            count: statsData.companies,
            time: 'recently',
            icon: faCity,
            color: '#14b8a6',
            timestamp: new Date().toISOString()
          });
        }

        // Fetch real successful hires with actual data
        try {
          // Try to get recent successful hires from applications
          let hiresResponse = null;
          try {
            hiresResponse = await deduplicateRequest(
              'applications_hired_100',
              () => axios.get(buildApiUrl('/api/applications?status=hired&limit=100'), {
                headers: { 'Content-Type': 'application/json' }
              })
            );
          } catch (apiError) {
            // Silently handle missing or broken applications endpoint
            // This is optional data, so we'll just skip it
            hiresResponse = null;
          }
          
          if (hiresResponse && Array.isArray(hiresResponse.data) && hiresResponse.data.length > 0) {
            // Get most recent hire
            const recentHires = hiresResponse.data
              .filter(h => {
                const hireDate = h.appliedAt || h.createdAt || h.created_at || h.updatedAt || h.updated_at;
                if (!hireDate) return false;
                const hoursAgo = (now - new Date(hireDate)) / (1000 * 60 * 60);
                return hoursAgo <= 24;
              })
              .sort((a, b) => {
                const dateA = new Date(a.appliedAt || a.createdAt || a.created_at || 0);
                const dateB = new Date(b.appliedAt || b.createdAt || b.created_at || 0);
                return dateB - dateA;
              });
            
            if (recentHires.length > 0) {
              const mostRecentHire = recentHires[0];
              const hireDate = new Date(mostRecentHire.appliedAt || mostRecentHire.createdAt || mostRecentHire.created_at);
              const minutesAgo = Math.floor((now - hireDate) / (1000 * 60));
              const hoursAgo = Math.floor((now - hireDate) / (1000 * 60 * 60));
              
              let timeText = '';
              if (minutesAgo < 1) {
                timeText = 'just now';
              } else if (minutesAgo < 60) {
                timeText = minutesAgo === 1 ? '1 minute ago' : `${minutesAgo} minutes ago`;
              } else if (hoursAgo < 24) {
                timeText = hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
              } else {
                timeText = 'today';
              }
              
              // Try to get location from user profile or job
              const cities = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi', 'Kitale', 'Garissa', 'Kakamega', 'Nyeri'];
              const location = mostRecentHire.location || 
                              mostRecentHire.city || 
                              mostRecentHire.jobLocation ||
                              cities[Math.floor(Math.random() * cities.length)];
              
              activities.push({
                type: 'job_found',
                message: 'just found a job',
                location: location,
                time: timeText,
                icon: faTrophy,
                color: '#14b8a6',
                timestamp: hireDate.toISOString()
              });
            }
          } else if (statsData && statsData.successfulHires > 0) {
            // Fallback: Use stats data
            const cities = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi', 'Kitale', 'Garissa'];
            const randomCity = cities[Math.floor(Math.random() * cities.length)];
            
            // Calculate time based on successful hires count
            const timeAgo = statsData.successfulHires > 20 ? '2 minutes ago' : 
                           statsData.successfulHires > 10 ? '5 minutes ago' : 
                           statsData.successfulHires > 5 ? '10 minutes ago' : '30 minutes ago';
            
            activities.push({
              type: 'job_found',
              message: 'just found a job',
              location: randomCity,
              time: timeAgo,
              icon: faTrophy,
              color: '#14b8a6',
              timestamp: new Date(Date.now() - (statsData.successfulHires > 20 ? 120000 : 300000)).toISOString()
            });
          }
        } catch (error) {
          console.error('Error fetching successful hires:', error);
        }

        // Remove duplicates by type (keep the first/most recent one)
        const seenTypes = new Set();
        const uniqueActivities = activities.filter(activity => {
          if (seenTypes.has(activity.type)) {
            return false; // Skip duplicate
          }
          seenTypes.add(activity.type);
          return true;
        });

        // Sort by timestamp (most recent first) and take top 4
        uniqueActivities.sort((a, b) => {
          const timeA = new Date(a.timestamp || 0).getTime();
          const timeB = new Date(b.timestamp || 0).getTime();
          return timeB - timeA;
        });

        // Get existing activity types to avoid duplicates when filling with defaults
        const existingTypes = new Set(uniqueActivities.map(a => a.type));

        // Ensure we have at least 4 activities (fill with defaults if needed)
        const defaultActivities = [
          { type: 'job_posted', message: 'new jobs posted', count: statsData?.activeJobs || 3, time: '1 hour ago', icon: faSuitcase, color: '#f97316', timestamp: new Date(Date.now() - 3600000).toISOString() },
          { type: 'application', message: 'applications submitted', count: statsData?.jobSeekers ? Math.floor(statsData.jobSeekers * 0.3) : 8, time: '2 hours ago', icon: faClipboardCheck, color: '#f97316', timestamp: new Date(Date.now() - 7200000).toISOString() },
          { type: 'company_joined', message: 'employers joined', count: statsData?.companies || 2, time: '3 hours ago', icon: faCity, color: '#14b8a6', timestamp: new Date(Date.now() - 10800000).toISOString() },
          { type: 'job_found', message: 'just found a job', location: 'Nairobi', time: '5 minutes ago', icon: faTrophy, color: '#14b8a6', timestamp: new Date().toISOString() }
        ];

        // Filter out default activities that already exist
        const uniqueDefaults = defaultActivities.filter(defaultAct => !existingTypes.has(defaultAct.type));

        const finalActivities = uniqueActivities.length >= 4 
          ? uniqueActivities.slice(0, 4) 
          : [...uniqueActivities, ...uniqueDefaults.slice(0, 4 - uniqueActivities.length)];

        setRecentActivity(finalActivities);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        // Fallback to default activities with stats if available
        try {
          const statsResponse = await deduplicateRequest(
            'stats_summary',
            () => fetch(buildApiUrl('/api/public/stats-summary'))
          );
          if (statsResponse.ok) {
            const data = await statsResponse.json();
            if (data?.success && data.data) {
              const stats = data.data;
              setRecentActivity([
                { type: 'job_posted', message: 'new jobs posted', count: stats.activeJobs || 5, time: '15 minutes ago', icon: faSuitcase, color: '#f97316' },
                { type: 'application', message: 'applications submitted', count: stats.jobSeekers ? Math.floor(stats.jobSeekers * 0.3) : 12, time: '2 hours ago', icon: faClipboardCheck, color: '#f97316' },
                { type: 'company_joined', message: 'employers joined', count: stats.companies || 3, time: '1 hour ago', icon: faCity, color: '#14b8a6' },
                { type: 'job_found', message: 'just found a job', location: 'Nairobi', time: '2 minutes ago', icon: faTrophy, color: '#14b8a6' }
              ]);
              return;
            }
          }
        } catch (e) {
          console.error('Error in fallback:', e);
        }
        // Final fallback
        setRecentActivity([
          { type: 'job_posted', message: 'new jobs posted', count: 5, time: '15 minutes ago', icon: faSuitcase, color: '#f97316' },
          { type: 'application', message: 'applications submitted', count: 12, time: '2 hours ago', icon: faClipboardCheck, color: '#f97316' },
          { type: 'company_joined', message: 'employers joined', count: 3, time: '1 hour ago', icon: faCity, color: '#14b8a6' },
          { type: 'job_found', message: 'just found a job', location: 'Nairobi', time: '2 minutes ago', icon: faTrophy, color: '#14b8a6' }
        ]);
      }
    };

    fetchRecentActivity();
    // Update activity feed every 60 seconds
    const interval = setInterval(fetchRecentActivity, 60000);
    return () => clearInterval(interval);
  }, []);

  // Toast management
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const closeToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Form handlers
  const handleSearch = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const hasQuery = searchQuery.trim().length > 0;
    const hasLocation = searchLocation.trim().length > 0;
    if (hasQuery || hasLocation) {
      const msg = hasQuery && hasLocation
        ? `🔍 Searching for "${searchQuery}" in ${searchLocation}...`
        : hasQuery
          ? `🔍 Searching for "${searchQuery}"...`
          : `🔍 Searching jobs in ${searchLocation}...`;
      showToast(msg, 'success');
      navigate('/jobs', { state: { query: searchQuery.trim(), location: searchLocation.trim() } });
    }
  };

  const handleCategoryClick = (category) => {
    showToast(`🔍 Searching for ${category} jobs...`, 'success');
    navigate('/jobs', { state: { category } });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      handleSearch(e);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Fetch job suggestions as user types
  const fetchJobSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setJobSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('query', query);
      if (searchLocation) {
        params.append('location', searchLocation);
      }
      
      const response = await axios.get(buildApiUrl(`/api/jobs/get_jobs?${params.toString()}`));
      const jobs = Array.isArray(response.data) ? response.data : [];
      setJobSuggestions(jobs.slice(0, 5)); // Show top 5 suggestions
      setShowSuggestions(jobs.length > 0);
    } catch (error) {
      console.error('Error fetching job suggestions:', error);
      setJobSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle search query change with debounce
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      fetchJobSuggestions(value);
    }, 300); // 300ms delay
  };

  // Handle selecting a job from suggestions
  const handleJobSuggestionClick = (job) => {
    const jobTitle = job.job_title || job.title || '';
    setSearchQuery(jobTitle);
    setShowSuggestions(false);
    setJobSuggestions([]);
    // Navigate to search results
    navigate('/jobs', { state: { query: jobTitle, location: searchLocation } });
  };

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setTestimonialsLoading(true);
        const data = await fetchPublicTestimonials();
        const formatted = (Array.isArray(data) ? data : [])
          .map(mapTestimonialPayload)
          .filter((item) => item && item.quote && item.author);
        setTestimonials(formatted);
        setCurrentTestimonial(0);
      } catch (error) {
        console.error('❌ Error fetching testimonials:', error);
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  const activeTestimonial = testimonials.length
    ? testimonials[currentTestimonial % testimonials.length]
    : null;

  useEffect(() => {
    if (!testimonials.length) return undefined;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 10000); // Show each testimonial for 10 seconds
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    if (!testimonials.length) return;
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (!testimonials.length) return;
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-field-group')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup search timeout on unmount
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch featured jobs
  const handleNavigateToJob = (jobId, applyUrl) => {
    if (applyUrl) {
      window.open(applyUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!jobId) {
      showToast('Job details are temporarily unavailable.', 'error');
      return;
    }

    // Navigate to job details - unauthenticated users will see sign-up prompt if job can't be loaded
    navigate(`/job-details/${jobId}`);
  };

  useEffect(() => {
    const fetchFeaturedJobs = async () => {
      try {
        setLoadingJobs(true);
        const response = await axios.get(buildApiUrl('/api/public/featured-jobs'));
        const payload = response.data;

        if (payload?.success && Array.isArray(payload.data)) {
          // Filter out internships from featured jobs
          const filteredJobs = payload.data.filter(job => {
            const jobType = job.job_type || job.jobType || job.type || '';
            const jobTitle = (job.job_title || job.title || '').toLowerCase();
            
            // Exclude internships
            return jobType.toLowerCase() !== 'internship' && 
                   !jobTitle.includes('intern') &&
                   jobType.toLowerCase() !== 'intern';
          });
          
          setFeaturedJobs(filteredJobs);
        } else {
          console.warn('⚠️ Featured jobs response did not include data array.', payload);
          setFeaturedJobs([]);
        }
      } catch (error) {
        console.error('❌ Error fetching featured jobs:', error);
        if (error.response) {
          console.error('Response error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('Request error: No response received');
        } else {
          console.error('Error message:', error.message);
        }
        setFeaturedJobs([]);
        showToast('⚠️ Could not load featured jobs. Please try again later.', 'error');
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchFeaturedJobs();
  }, []);

  useEffect(() => {
    const fetchFeaturedInternships = async () => {
      try {
        setLoadingInternships(true);
        const response = await axios.get(buildApiUrl('/api/public/featured-internships'));
        const payload = response.data;

        if (payload?.success && Array.isArray(payload.data)) {
          setFeaturedInternships(payload.data);
        } else if (Array.isArray(payload)) {
          setFeaturedInternships(payload);
        } else {
          console.warn('⚠️ Featured internships response did not include data array.', payload);
          setFeaturedInternships([]);
        }
      } catch (error) {
        console.error('❌ Error fetching featured internships:', error);
        setFeaturedInternships([]);
        showToast('⚠️ Could not load featured internships. Please try again later.', 'error');
      } finally {
        setLoadingInternships(false);
      }
    };

    fetchFeaturedInternships();
  }, []);

  useEffect(() => {
    const fetchHomeEvents = async () => {
      try {
        setLoadingUpcomingEvent(true);
        const response = await axios.get(buildApiUrl('/api/public/events?sort=latest&limit=30'));
        const list =
          response.data?.success && Array.isArray(response.data.events) ? response.data.events : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const eligible = list
          .map((ev) => ({ ev, d: parsePublicEventDate(ev.eventDate ?? ev.date) }))
          .filter(({ ev, d }) => shouldShowOnHomepage(ev, d, today));

        const sorted = sortHomepageEvents(eligible);
        setHomeEvents(sorted.slice(0, 4).map(({ ev }) => ev));
      } catch (error) {
        console.error('Error fetching homepage events:', error);
        setHomeEvents([]);
      } finally {
        setLoadingUpcomingEvent(false);
      }
    };

    fetchHomeEvents();
  }, []);

  const carouselEvents = React.useMemo(
    () => homeEvents.filter((ev) => getEventStatus(ev) === 'upcoming'),
    [homeEvents]
  );

  const totalHomeEventSlides = carouselEvents.length;
  const currentHomeEvent = carouselEvents[homeEventsSlideIndex] || null;

  useEffect(() => {
    setHomeEventsSlideIndex(0);
  }, [carouselEvents]);

  useEffect(() => {
    if (homeEventsSlideIndex >= totalHomeEventSlides && totalHomeEventSlides > 0) {
      setHomeEventsSlideIndex(totalHomeEventSlides - 1);
    }
  }, [homeEventsSlideIndex, totalHomeEventSlides]);

  useEffect(() => {
    if (totalHomeEventSlides <= 1 || loadingUpcomingEvent) return undefined;
    const interval = setInterval(() => {
      setHomeEventsSlideIndex((prev) => (prev + 1) % totalHomeEventSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [totalHomeEventSlides, loadingUpcomingEvent]);

  const renderHomeEventCard = (event) => {
    if (!event) return null;
    const statusLabel = formatEventStatusLabel(event.status);
    const statusClass = getEventStatus(event);
    return (
      <Link
        to={`/events?event=${encodeURIComponent(event.id || event._id)}`}
        className="upcoming-event-card"
      >
        {event.posterUrl && (
          <img
            src={event.posterUrl}
            alt=""
            className="upcoming-event-poster"
            loading="lazy"
          />
        )}
        <div className="upcoming-event-info">
          {statusLabel && (
            <span className={`upcoming-event-status ${statusClass}`}>
              {statusLabel}
            </span>
          )}
          <p className="upcoming-event-title">{event.title}</p>
          {(event.eventDate || event.date) && (
            <p className="upcoming-event-meta">
              <FontAwesomeIcon icon={faCalendarAlt} />
              {new Date(event.eventDate || event.date).toLocaleDateString(
                undefined,
                { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
              )}
            </p>
          )}
          {(event.eventTime || event.time) && (
            <p className="upcoming-event-meta">
              <FontAwesomeIcon icon={faClock} />
              {formatEventTimeDisplay(event.eventTime || event.time)}
            </p>
          )}
          {(event.venue || event.location) && (
            <p className="upcoming-event-meta">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {event.venue || event.location}
            </p>
          )}
        </div>
      </Link>
    );
  };

  // Fetch popular searches and trending locations
  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        // Fetch jobs to analyze popular searches
        const response = await axios.get(buildApiUrl('/api/jobs/get_jobs?limit=200'));
        const jobs = Array.isArray(response.data) ? response.data : [];
        
        // Extract popular job titles
        const jobTitleCounts = {};
        const locationCounts = {};
        const countryCounts = {};
        
        jobs.forEach(job => {
          // Count job titles
          const title = job.job_title || job.title || '';
          if (title) {
            const normalizedTitle = title.trim();
            if (normalizedTitle) {
              jobTitleCounts[normalizedTitle] = (jobTitleCounts[normalizedTitle] || 0) + 1;
            }
          }
          
          // Extract location from job - support location, city, country, state
          let locationStr = '';
          let city = '';
          let country = '';
          if (typeof job.location === 'string') {
            locationStr = job.location.trim();
          } else if (job.location?.city || job.location?.country) {
            city = (job.location.city || '').trim();
            country = (job.location.country || '').trim();
            locationStr = [city, country].filter(Boolean).join(', ');
          }
          if (!locationStr && (job.city || job.country)) {
            city = (job.city || '').trim();
            country = (job.country || '').trim();
            locationStr = [city, country].filter(Boolean).join(', ');
          }
          // Also check workMode for Remote/Hybrid
          if (!locationStr && job.workMode) {
            locationStr = String(job.workMode).trim();
          }
          
          if (locationStr) {
            const parts = locationStr.split(',').map(p => p.trim()).filter(Boolean);
            const uniqueParts = [...new Set(parts.map(p => p.toLowerCase()))].map(p => 
              parts.find(orig => orig.toLowerCase() === p) || p
            );
            
            // Count country for "Trending in [Country]" label (once per job)
            const lastPart = uniqueParts[uniqueParts.length - 1];
            const firstPart = uniqueParts[0];
            const skipCountryKeywords = ['remote', 'hybrid', 'onsite', 'on-site', 'anywhere', 'work from home'];
            const countryToCount = country || (lastPart && lastPart.length > 2 && lastPart.length < 50 && !skipCountryKeywords.some(kw => lastPart.toLowerCase().includes(kw)) ? lastPart : null);
            if (countryToCount) {
              const countryName = countryToCount.replace(/\b\w/g, c => c.toUpperCase());
              countryCounts[countryName] = (countryCounts[countryName] || 0) + 1;
            }
            
            // Count city/primary location for chips
            const displayLocation = firstPart && firstPart.length <= 50 
              ? firstPart.replace(/\b\w/g, c => c.toUpperCase()) 
              : locationStr;
            const skipKeywords = ['remote', 'hybrid', 'onsite', 'on-site', 'anywhere', 'work from home'];
            const isGeneric = skipKeywords.some(kw => displayLocation.toLowerCase().includes(kw));
            const key = isGeneric ? displayLocation : (uniqueParts[0]?.replace(/\b\w/g, c => c.toUpperCase()) || displayLocation);
            if (key && key.length <= 50) {
              locationCounts[key] = (locationCounts[key] || 0) + 1;
            }
          }
        });
        
        // Get top 6 popular job titles (remove duplicates, exclude known typos)
        const uniqueTitles = new Set();
        const excludeFromPopular = new Set(['accountaantg']);
        const popularTitles = Object.entries(jobTitleCounts)
          .sort((a, b) => b[1] - a[1])
          .filter(([title]) => {
            const normalized = title.toLowerCase().trim();
            if (excludeFromPopular.has(normalized)) return false;
            if (!uniqueTitles.has(normalized)) {
              uniqueTitles.add(normalized);
              return true;
            }
            return false;
          })
          .slice(0, 6)
          .map(([title]) => title);
        
        // Get top country for "Trending in [Country]" label
        const topCountryEntry = Object.entries(countryCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 1)[0];
        const topCountry = topCountryEntry ? topCountryEntry[0] : 'Kenya';
        
        // Get top 5 trending locations (cities/countries from posted jobs - worldwide)
        const uniqueLocs = new Set();
        const trendingLocs = Object.entries(locationCounts)
          .sort((a, b) => b[1] - a[1])
          .filter(([loc]) => {
            const normalized = loc.toLowerCase().trim();
            if (!uniqueLocs.has(normalized) && loc.length >= 2) {
              uniqueLocs.add(normalized);
              return true;
            }
            return false;
          })
          .slice(0, 6)
          .map(([loc]) => loc);
        
        // Fallback to default if no data
        setPopularSearches(popularTitles.length > 0 ? popularTitles : [
          'Software Engineer',
          'Data Analyst',
          'Marketing Manager',
          'Sales Executive',
          'Accountant',
          'Project Manager'
        ]);
        
        setTrendingCountry(topCountry);
        setTrendingLocations(trendingLocs.length > 0 ? trendingLocs : [
          'Nairobi',
          'Mombasa',
          'Kisumu',
          'Nakuru',
          'Eldoret'
        ]);
      } catch (error) {
        console.error('Error fetching trending data:', error);
        setPopularSearches([
          'Software Engineer',
          'Data Analyst',
          'Marketing Manager',
          'Sales Executive',
          'Accountant',
          'Project Manager'
        ]);
        setTrendingCountry('Kenya');
        setTrendingLocations([
          'Nairobi',
          'Mombasa',
          'Kisumu',
          'Nakuru',
          'Eldoret'
        ]);
      }
    };
    
    fetchTrendingData();
  }, []);

  // Fetch job counts by category
  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        setCategoryLoading(true);
        // Fetch all jobs (including internships which have job_type='Internship')
        const jobsResponse = await deduplicateRequest(
          'homepage_category_jobs_100',
          () => axios.get(buildApiUrl('/api/jobs/get_jobs?limit=100'))
        ).catch(() => ({ data: [] })); // Reduced from 1000 to 100
        const jobs = Array.isArray(jobsResponse.data) ? jobsResponse.data : [];
        
        console.log('📦 Jobs fetched:', jobs.length);
        
        // Also try to fetch internships from the internships collection
        let internships = [];
        try {
          const internshipsResponse = await axios.get(buildApiUrl('/api/public/featured-internships'));
          if (internshipsResponse.data?.success && Array.isArray(internshipsResponse.data.data)) {
            internships = internshipsResponse.data.data;
          } else if (Array.isArray(internshipsResponse.data)) {
            internships = internshipsResponse.data;
          }
        } catch (e) {
          console.log('Could not fetch separate internships, using jobs only');
        }
        
        // Deduplicate jobs by ID to avoid counting the same job twice
        const jobMap = new Map();
        [...jobs, ...internships].forEach(job => {
          const jobId = job._id || job.id || JSON.stringify(job);
          if (!jobMap.has(jobId)) {
            jobMap.set(jobId, job);
          }
        });
        const allJobs = Array.from(jobMap.values());
        
        console.log('📦 Total unique jobs/internships to process:', allJobs.length);
        
        // Map standardized industries to homepage categories
        // This ensures each job is counted in exactly ONE category
        // Priority order matters - more specific matches should come first
        const mapIndustryToCategory = (normalizedIndustry, jobTitle = '') => {
          const industryLower = normalizedIndustry.toLowerCase();
          const titleLower = jobTitle.toLowerCase();
          
          // Check for exact matches first (most specific)
          if (industryLower === 'technology & it' || 
              industryLower === 'telecommunications') {
            return 'Technology & IT';
          }
          
          if (industryLower === 'healthcare & medical' ||
              industryLower === 'pharmaceutical & biotech') {
            return 'Healthcare';
          }
          
          if (industryLower === 'finance & banking' ||
              industryLower === 'insurance') {
            return 'Finance & Accounting';
          }
          
          if (industryLower === 'marketing & advertising' ||
              industryLower === 'sales & business development') {
            return 'Marketing & Sales';
          }
          
          if (industryLower === 'education & training') {
            return 'Education';
          }
          
          if (industryLower === 'manufacturing & production' ||
              industryLower === 'construction & real estate' ||
              industryLower === 'hospitality & tourism' ||
              industryLower === 'retail & e-commerce' ||
              industryLower === 'legal services' ||
              industryLower === 'consulting & professional services') {
            const map = {
              'manufacturing & production': 'Engineering & Manufacturing',
              'construction & real estate': 'Construction & Real Estate',
              'hospitality & tourism': 'Hospitality & Tourism',
              'retail & e-commerce': 'Retail & E-commerce',
              'legal services': 'Legal Services',
              'consulting & professional services': 'Consulting & Professional Services'
            };
            return map[industryLower];
          }
          
          if (industryLower === 'human resources') {
            return 'Human Resources';
          }
          
          // Then check for partial matches in industry
          // Technology & IT
          if (industryLower.includes('technology') || 
              industryLower.includes('software') ||
              (industryLower.includes('tech') && !industryLower.includes('biotech')) ||
              industryLower.includes('information technology') ||
              industryLower.includes(' it ') ||
              industryLower.startsWith('it ') ||
              industryLower.endsWith(' it') ||
              industryLower === 'it' ||
              industryLower.includes('computer') ||
              industryLower.includes('programming') ||
              industryLower.includes('developer') ||
              (industryLower.includes('engineering') && (industryLower.includes('software') || industryLower.includes('tech')))) {
            return 'Technology & IT';
          }
          
          // Healthcare
          if (industryLower.includes('healthcare') ||
              industryLower.includes('health') ||
              industryLower.includes('medical') ||
              industryLower.includes('hospital') ||
              industryLower.includes('pharmaceutical') ||
              industryLower.includes('pharma') ||
              industryLower.includes('biotech') ||
              industryLower.includes('clinical')) {
            return 'Healthcare';
          }
          
          // Finance & Accounting
          if (industryLower.includes('finance') ||
              industryLower.includes('banking') ||
              industryLower.includes('accounting') ||
              industryLower.includes('accountant') ||
              industryLower.includes('financial') ||
              industryLower.includes('audit') ||
              industryLower.includes('bookkeeping')) {
            return 'Finance & Accounting';
          }
          
          // Design & Creative
          if (industryLower.includes('design') ||
              industryLower.includes('creative') ||
              industryLower.includes('graphic') ||
              industryLower.includes('ui/ux') ||
              industryLower.includes('art') ||
              industryLower.includes('illustration') ||
              industryLower.includes('multimedia') ||
              industryLower.includes('fashion') ||
              industryLower.includes('apparel')) {
            return 'Design & Creative';
          }
          
          // Marketing & Sales
          if (industryLower.includes('marketing') ||
              industryLower.includes('advertising') ||
              industryLower.includes('sales') ||
              industryLower.includes('business development') ||
              industryLower.includes('promotion') ||
              industryLower.includes('brand')) {
            return 'Marketing & Sales';
          }
          
          // Education
          if (industryLower.includes('education') ||
              industryLower.includes('training') ||
              industryLower.includes('teaching') ||
              industryLower.includes('academic') ||
              industryLower.includes('school')) {
            return 'Education';
          }
          
          // Engineering & Manufacturing
          if (industryLower.includes('manufacturing') ||
              industryLower.includes('production') ||
              industryLower.includes('engineering') ||
              industryLower.includes('industrial') ||
              industryLower.includes('automotive') ||
              industryLower.includes('mechanical')) {
            return 'Engineering & Manufacturing';
          }
          
          // Legal Services
          if (industryLower.includes('legal') ||
              industryLower.includes('law') ||
              industryLower.includes('attorney') ||
              industryLower.includes('compliance')) {
            return 'Legal Services';
          }
          
          // Hospitality & Tourism
          if (industryLower.includes('hospitality') ||
              industryLower.includes('tourism') ||
              industryLower.includes('hotel') ||
              industryLower.includes('restaurant') ||
              industryLower.includes('food & beverage') ||
              industryLower.includes('travel')) {
            return 'Hospitality & Tourism';
          }
          
          // Consumer Goods
          if (industryLower.includes('consumer goods') ||
              industryLower.includes('fmcg') ||
              industryLower.includes('cpg') ||
              industryLower.includes('fast moving') ||
              industryLower.includes('consumer products') ||
              industryLower.includes('financial controller')) {
            return 'Consumer Goods';
          }

          // Logistics & Supply Chain
          if (industryLower.includes('logistics') ||
              industryLower.includes('supply chain') ||
              industryLower.includes('transport') ||
              industryLower.includes('shipping') ||
              industryLower.includes('warehouse') ||
              industryLower.includes('procurement')) {
            return 'Logistics & Supply Chain';
          }
          
          // Retail & E-commerce
          if (industryLower.includes('retail') ||
              industryLower.includes('e-commerce') ||
              industryLower.includes('ecommerce')) {
            return 'Retail & E-commerce';
          }
          
          // Human Resources
          if (industryLower.includes('human resources') ||
              industryLower.includes('hr ') ||
              industryLower.includes('recruitment') ||
              industryLower.includes('staffing')) {
            return 'Human Resources';
          }
          
          // Consulting & Professional Services
          if (industryLower.includes('consulting') ||
              industryLower.includes('professional services') ||
              industryLower.includes('business services')) {
            return 'Consulting & Professional Services';
          }
          
          // Construction & Real Estate
          if (industryLower.includes('construction') ||
              industryLower.includes('real estate') ||
              industryLower.includes('property')) {
            return 'Construction & Real Estate';
          }
          
          // Fallback: Check job title if industry didn't match
          if (titleLower) {
            // Technology & IT
            if (titleLower.includes('software') ||
                titleLower.includes('developer') ||
                titleLower.includes('programmer') ||
                (titleLower.includes('engineer') && (titleLower.includes('software') || titleLower.includes('tech'))) ||
                titleLower.includes(' it ') ||
                titleLower.startsWith('it ') ||
                titleLower.endsWith(' it') ||
                titleLower === 'it' ||
                titleLower.includes('data engineer') ||
                titleLower.includes('web developer') ||
                titleLower.includes('systems engineer') ||
                titleLower.includes('network engineer')) {
              return 'Technology & IT';
            }
            
            // Healthcare
            if (titleLower.includes('nurse') ||
                titleLower.includes('doctor') ||
                titleLower.includes('physician') ||
                titleLower.includes('medical') ||
                titleLower.includes('healthcare') ||
                titleLower.includes('pharmacist')) {
              return 'Healthcare';
            }
            
            // Finance & Accounting
            if (titleLower.includes('accountant') ||
                titleLower.includes('financial') ||
                titleLower.includes('auditor') ||
                titleLower.includes('bookkeeper') ||
                titleLower.includes('finance') ||
                titleLower.includes('banking')) {
              return 'Finance & Accounting';
            }
            
            // Design & Creative
            if (titleLower.includes('designer') ||
                titleLower.includes('graphic') ||
                titleLower.includes('ui designer') ||
                titleLower.includes('ux designer') ||
                titleLower.includes('creative')) {
              return 'Design & Creative';
            }
            
            // Marketing & Sales
            if (titleLower.includes('marketing') ||
                titleLower.includes('sales') ||
                titleLower.includes('advertising') ||
                titleLower.includes('business development')) {
              return 'Marketing & Sales';
            }
            
            // Education
            if (titleLower.includes('teacher') ||
                titleLower.includes('tutor') ||
                titleLower.includes('instructor') ||
                titleLower.includes('lecturer') ||
                titleLower.includes('professor')) {
              return 'Education';
            }
            
            // Engineering & Manufacturing
            if (titleLower.includes('manufacturing') ||
                titleLower.includes('mechanical engineer') ||
                titleLower.includes('production') ||
                titleLower.includes('quality engineer') ||
                titleLower.includes('industrial')) {
              return 'Engineering & Manufacturing';
            }
            
            // Legal Services
            if (titleLower.includes('lawyer') ||
                titleLower.includes('legal') ||
                titleLower.includes('attorney') ||
                titleLower.includes('paralegal')) {
              return 'Legal Services';
            }
            
            // Hospitality & Tourism
            if (titleLower.includes('chef') ||
                titleLower.includes('hotel') ||
                titleLower.includes('restaurant') ||
                titleLower.includes('tourism') ||
                titleLower.includes('hospitality')) {
              return 'Hospitality & Tourism';
            }
            
            // Consumer Goods
            if (titleLower.includes('consumer goods') ||
                titleLower.includes('fmcg') ||
                titleLower.includes('brand manager') ||
                titleLower.includes('product manager') ||
                titleLower.includes('cpg') ||
                titleLower.includes('financial controller')) {
              return 'Consumer Goods';
            }

            // Logistics & Supply Chain
            if (titleLower.includes('logistics') ||
                titleLower.includes('supply chain') ||
                titleLower.includes('transport') ||
                titleLower.includes('shipping') ||
                titleLower.includes('warehouse') ||
                titleLower.includes('procurement')) {
              return 'Logistics & Supply Chain';
            }
            
            // Retail & E-commerce
            if (titleLower.includes('retail') ||
                titleLower.includes('store manager') ||
                titleLower.includes('e-commerce')) {
              return 'Retail & E-commerce';
            }
            
            // Human Resources
            if (titleLower.includes('hr ') ||
                titleLower.includes('human resources') ||
                titleLower.includes('recruiter') ||
                titleLower.includes('talent')) {
              return 'Human Resources';
            }
            
            // Consulting & Professional Services
            if (titleLower.includes('consultant') ||
                titleLower.includes('consulting')) {
              return 'Consulting & Professional Services';
            }
            
            // Construction & Real Estate
            if (titleLower.includes('construction') ||
                titleLower.includes('real estate') ||
                titleLower.includes('civil engineer')) {
              return 'Construction & Real Estate';
            }
          }
          
          // If no match found, return null (job won't be counted in any category)
          return null;
        };
        
        // Initialize counts
        const counts = {
          'Technology & IT': 0,
          'Healthcare': 0,
          'Finance & Accounting': 0,
          'Design & Creative': 0,
          'Marketing & Sales': 0,
          'Education': 0,
          'Engineering & Manufacturing': 0,
          'Legal Services': 0,
          'Hospitality & Tourism': 0,
          'Consumer Goods': 0,
          'Logistics & Supply Chain': 0,
          'Retail & E-commerce': 0,
          'Human Resources': 0,
          'Consulting & Professional Services': 0,
          'Construction & Real Estate': 0
        };
        
        // Track unmatched jobs for debugging
        const unmatchedJobs = [];
        
        // Count jobs by category - each job is counted exactly once
        allJobs.forEach((job) => {
          // Get industry from various possible fields
          let rawIndustry = job.industry || job.category || job.domain || job.industryType || '';
          
          // Handle if industry is an array (take first element)
          if (Array.isArray(rawIndustry)) {
            rawIndustry = rawIndustry[0] || '';
          }
          
          // Handle if industry is an object
          if (typeof rawIndustry === 'object' && rawIndustry !== null) {
            rawIndustry = rawIndustry.name || rawIndustry.value || rawIndustry.toString() || '';
          }
          
          // Ensure it's a string
          rawIndustry = String(rawIndustry || '').trim();
          
          // Normalize the industry
          const normalizedIndustry = normalizeIndustry(rawIndustry);
          const jobTitle = job.job_title || job.title || '';
          
          // Map to category
          const category = mapIndustryToCategory(normalizedIndustry, jobTitle);
          
          if (category) {
            counts[category] = (counts[category] || 0) + 1;
          } else if (rawIndustry) {
            // Track unmatched for debugging
            unmatchedJobs.push({
              title: jobTitle,
              industry: rawIndustry,
              normalized: normalizedIndustry
            });
          }
        });
        
        // Log results
        console.log('📊 Category counts:', counts);
        console.log('📊 Total jobs processed:', allJobs.length);
        console.log('📊 Unmatched jobs:', unmatchedJobs.length);
        if (unmatchedJobs.length > 0 && unmatchedJobs.length <= 20) {
          console.log('📋 Unmatched jobs sample:', unmatchedJobs);
        }
        
        // Verify counts add up (they should be <= total jobs since some may not match)
        const totalCounted = Object.values(counts).reduce((sum, count) => sum + count, 0);
        console.log(`📊 Total jobs counted: ${totalCounted} out of ${allJobs.length}`);
        
        setCategoryCounts(counts);
      } catch (error) {
        console.error('Error fetching category counts:', error);
        // Set default counts to 0 on error
        setCategoryCounts({
          'Technology & IT': 0,
          'Healthcare': 0,
          'Finance & Accounting': 0,
          'Design & Creative': 0,
          'Marketing & Sales': 0,
          'Education': 0,
          'Engineering & Manufacturing': 0,
          'Legal Services': 0,
          'Hospitality & Tourism': 0,
          'Consumer Goods': 0,
          'Logistics & Supply Chain': 0,
          'Retail & E-commerce': 0,
          'Human Resources': 0,
          'Consulting & Professional Services': 0,
          'Construction & Real Estate': 0
        });
      } finally {
        setCategoryLoading(false);
      }
    };
    
    fetchCategoryCounts();
  }, []);

  const isJobsView = activeFeaturedTab === 'jobs';
  const isInternshipView = !isJobsView;
  const activeFeaturedList = isJobsView ? featuredJobs : featuredInternships;
  const loadingFeatured = isJobsView ? loadingJobs : loadingInternships;
  const featuredHeading = isJobsView ? 'Featured Jobs' : 'Featured Internships';
  const featuredSubtitle = isJobsView
    ? 'Hand-picked opportunities from top companies'
    : 'Meaningful internship experiences with trusted organizations';
  const featuredEmptyMessage = isJobsView
    ? 'No jobs available at the moment. Check back soon!'
    : 'No internships available at the moment. Check back soon!';
  const featuredLoadingMessage = isJobsView
    ? 'Loading featured jobs...'
    : 'Loading featured internships...';
  const viewAllLink = isJobsView ? '/jobseeker-dashboard' : '/featured-internships';
  const viewAllLabel = isJobsView ? 'View All Jobs' : 'View All Internships';

  const popularLoopItems = Array.from(
    new Set(
      [...popularSearches, ...DEFAULT_POPULAR_SEARCHES]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  )
    .slice(0, 12)
    .map((label) => ({ label, type: 'popular' }));

  const locationLoopItems = Array.from(
    new Set(
      [...trendingLocations, ...DEFAULT_KENYA_TRENDING_LOCATIONS]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  )
    .slice(0, 12)
    .map((label) => ({ label, type: 'location' }));

  // Temporarily disable live activity and keep all values at zero.
  const displayedRecentActivity = [
    { type: 'job_posted', message: 'new jobs posted', count: 0, time: '—' },
    { type: 'application', message: 'applications submitted', count: 0, time: '—' },
    { type: 'company_joined', message: 'employers joined', count: 0, time: '—' },
    { type: 'placements', message: 'placements confirmed', count: 0, time: '—' }
  ];

  return (
    <div className="homepage">
      {/* Header */}
      <header>
        <div className="nav-container">
          <div className="logo">
            <img src={logoImage} alt="AksharJobs Logo" className="logo-image" />
            <div className="logo-text">
              <span className="logo-name">AksharJobs</span>
              <span className="logo-tagline">CONNECT | DISCOVER | ELEVATE</span>
              <span className="beta-version-text">BETA VERSION</span>
            </div>
          </div>
          <nav>
            <ul>
              <li><Link to="/about-platform">About Platform</Link></li>
              <li><Link to="/companies">Employers</Link></li>
              <li><Link to="/associations">Associations</Link></li>
              <li><Link to="/universities">Universities</Link></li>
              <li><Link to="/communities">Communities</Link></li>
              <li><Link to="/professional">Professionals</Link></li>
              <li><Link to="/ngos">NGOs</Link></li>
              <li><Link to="/volunteers">Volunteers</Link></li>
              <li><Link to="/events">Events</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/help-center">Help</Link></li>
            </ul>
          </nav>
          <div className="header-auth-buttons">
            <Link to="/login" className="header-cta-btn header-cta-secondary">
              SIGN IN
            </Link>
            <Link to="/signup" className="header-cta-btn header-cta-primary">
              SIGN UP
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <Aurora
            colorStops={["#ff8c42", "#ffa726", "#14b8a6"]}
            blend={0.5}
            amplitude={1.0}
            speed={0.5}
          />
        </div>
        <div className="hero-content">
          <h1>
            <span key={activeHeroHeadline} className="hero-rotating-headline">
              {HERO_HEADLINE_VARIANTS[activeHeroHeadline]}
            </span>{' '}
            With <span style={{ color: 'white' }}>AksharJobs</span>
          </h1>
          <p className="hero-tagline">CONNECT | DISCOVER | ELEVATE</p>
          <p className="hero-description" style={{ color: '#ffffff', opacity: 1 }}>Connect with thousands of employers as jobseekers and interns to discover opportunities that match your skills and aspirations</p>
        </div>
        
        <div className="hero-search-container">
          <form onSubmit={handleSearch} className="hero-search-form">
            <div className="hero-search-wrapper">
              <div className="search-field-group">
                <div className="search-field" style={{ position: 'relative' }}>
                  <input 
                    type="text" 
                    className="search-field-input" 
                    placeholder="Job title"
                    value={searchQuery}
                    onChange={handleQueryChange}
                    onKeyPress={handleKeyPress}
                    onFocus={() => {
                      if (jobSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    autoComplete="off"
                  />
                  {showSuggestions && jobSuggestions.length > 0 && (
                    <div className="job-suggestions-dropdown">
                      {jobSuggestions.map((job, index) => (
                        <div
                          key={job._id || index}
                          className="job-suggestion-item"
                          onClick={() => handleJobSuggestionClick(job)}
                        >
                          <div className="suggestion-content-wrapper">
                            <div className="suggestion-job-title">
                              {job.job_title || job.title || 'Untitled Job'}
                            </div>
                            <div className="suggestion-job-company">
                              {job.company_name || job.company || 'Company not specified'}
                            </div>
                            {job.location && (
                              <div className="suggestion-job-location">
                                {job.location}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="search-field">
                  <input 
                    type="text" 
                    className="search-field-input" 
                    placeholder="City, state"
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              </div>
              <button type="submit" className="hero-search-button">
                Search Jobs
              </button>
            </div>
          </form>
        </div>

        {/* Popular Searches & Trending Locations — full-bleed marquee (same as desktop) */}
        <div className="hero-trending-section">
          <div className="trending-group">
            <span className="trending-label">Popular searches:</span>
            <div className="hero-trending-loop">
              <LogoLoop
                logos={popularLoopItems}
                speed={52}
                direction="left"
                gap={12}
                logoHeight={32}
                hoverSpeed={0}
                fadeOut
                fadeOutColor="rgba(14, 116, 144, 0.15)"
                ariaLabel="Popular job searches"
                renderItem={(item, key) => (
                  <button
                    key={key}
                    type="button"
                    className="trending-chip btn-custom-size"
                    onClick={() => {
                      setSearchQuery(item.label);
                      navigate('/jobs', { state: { query: item.label, location: searchLocation } });
                    }}
                  >
                    {item.label}
                  </button>
                )}
              />
            </div>
          </div>
          <div className="trending-group">
            <span className="trending-label">Trending in {trendingCountry}:</span>
            <div className="hero-trending-loop">
              <LogoLoop
                logos={locationLoopItems}
                speed={46}
                direction="right"
                gap={12}
                logoHeight={32}
                hoverSpeed={0}
                fadeOut
                fadeOutColor="rgba(14, 116, 144, 0.15)"
                ariaLabel={`Trending locations in ${trendingCountry}`}
                renderItem={(item, key) => (
                  <button
                    key={key}
                    type="button"
                    className="trending-chip location-chip btn-custom-size"
                    onClick={() => {
                      setSearchLocation(item.label);
                      navigate('/jobs', { state: { query: searchQuery, location: item.label } });
                    }}
                  >
                    {item.label}
                  </button>
                )}
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Temporarily Disabled */}
      {/* 
      <section className="how-it-works-section">
        <div className="how-it-works-container">
          <div className="how-it-works-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Watch our tutorial video to see how easy it is to get started</p>
          </div>

          <div className="how-it-works-tabs">
            <button 
              className={`how-it-works-tab ${activeHowItWorksTab === 'jobseekers' ? 'active' : ''}`}
              onClick={() => setActiveHowItWorksTab('jobseekers')}
            >
              For Job Seekers
            </button>
            <button 
              className={`how-it-works-tab ${activeHowItWorksTab === 'employers' ? 'active' : ''}`}
              onClick={() => setActiveHowItWorksTab('employers')}
            >
              For Employers
            </button>
          </div>

          <div className="tutorial-video-container">
            {activeHowItWorksTab === 'jobseekers' ? (
              <div className="video-wrapper">
                <div className="video-placeholder">
                  <div className="video-embed">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
                      title="AksharJobs Tutorial - For Job Seekers"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
                <div className="video-info">
                  <h3>Get Started as a Job Seeker</h3>
                  <p>Learn how to create your profile, upload your resume, and find your dream job using our AI-powered matching system.</p>
                </div>
              </div>
            ) : (
              <div className="video-wrapper">
                <div className="video-placeholder">
                  <div className="video-embed">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
                      title="AksharJobs Tutorial - For Employers"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
                <div className="video-info">
                  <h3>Get Started as an Employer</h3>
                  <p>Discover how to post jobs, find qualified candidates, and hire top talent using our intelligent recruitment platform.</p>
                </div>
              </div>
            )}
          </div>

          <div className="how-it-works-cta">
            <Link to="/signup" className="btn btn-primary btn-large">
              Get Started Free
              <FontAwesomeIcon icon={faRocket} style={{ marginLeft: '8px' }} />
            </Link>
            <p className="cta-note">No credit card required • Free forever</p>
          </div>
        </div>
      </section>
      */}

      {/* Socials & Events Section */}
      <section className="socials-events-section">
        <div className="jobs-container">
          <aside className="socials-events-panel">
            <h2 className="socials-events-title">
              <FontAwesomeIcon icon={faBell} className="socials-events-title-icon" />
              <span className="socials-events-title-text section-title-live">Stay Updated</span>
            </h2>
            <p className="socials-events-subtitle">
              Follow our socials to stay updated for jobs and internships
            </p>

            <div className="social-qr-grid">
              <div className="social-qr-card">
                <img
                  src={`${process.env.PUBLIC_URL}/QR%20LINKELDN%20NEW.png`}
                  alt="Scan to follow AksharJobs on LinkedIn"
                  className="social-qr-image"
                  loading="lazy"
                />
                <span className="social-qr-label linkedin-label">
                  <FontAwesomeIcon icon={faLinkedin} />
                  LinkedIn
                </span>
              </div>
              <div className="social-qr-card">
                <img
                  src={`${process.env.PUBLIC_URL}/QR%20WHATSAPP.png`}
                  alt="Scan to join AksharJobs on WhatsApp"
                  className="social-qr-image"
                  loading="lazy"
                />
                <span className="social-qr-label whatsapp-label">
                  <FontAwesomeIcon icon={faWhatsapp} />
                  WhatsApp channel
                </span>
              </div>
              <div className="social-qr-card">
                <img
                  src={`${process.env.PUBLIC_URL}/QR%20INSTAGRAM.png`}
                  alt="Scan to follow AksharJobs on Instagram"
                  className="social-qr-image"
                  loading="lazy"
                />
                <span className="social-qr-label instagram-label">
                  <FontAwesomeIcon icon={faInstagram} />
                  Instagram
                </span>
              </div>
            </div>

            <div className="upcoming-event-box">
              <h3 className="upcoming-event-heading">Upcoming Events</h3>
              {loadingUpcomingEvent ? (
                <p className="upcoming-event-placeholder">Loading events...</p>
              ) : carouselEvents.length > 0 ? (
                <div className="home-events-carousel">
                  <div className="home-events-slide" key={homeEventsSlideIndex}>
                    {renderHomeEventCard(currentHomeEvent)}
                  </div>
                </div>
              ) : (
                <p className="upcoming-event-placeholder">No upcoming events right now.</p>
              )}
              <Link to="/events" className="upcoming-event-link">
                View all events
                <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Featured Opportunities Section */}
      <section className="featured-jobs" id="jobs">
        <div className="jobs-container">
          <div className="featured-jobs-main">
              <div className="featured-header">
                <div className="featured-header-text">
                  <h2 className="section-title section-title-live">{featuredHeading}</h2>
                  <p className="section-subtitle featured-subtitle-tight">{featuredSubtitle}</p>
                </div>
                <div className="featured-toggle" role="tablist" aria-label="Featured opportunities">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isJobsView}
                    aria-controls="featured-jobs-panel"
                    className={`featured-toggle-btn ${isJobsView ? 'active' : ''}`}
                    onClick={() => setActiveFeaturedTab('jobs')}
                  >
                    Jobs
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isInternshipView}
                    aria-controls="featured-internships-panel"
                    className={`featured-toggle-btn ${isInternshipView ? 'active' : ''}`}
                    onClick={() => setActiveFeaturedTab('internships')}
                  >
                    Internships
                  </button>
                </div>
              </div>

              {loadingFeatured ? (
                <div className="loading-jobs">
                  <div className="spinner"></div>
                  <p>{featuredLoadingMessage}</p>
                </div>
              ) : activeFeaturedList.length === 0 ? (
                <div className="no-jobs">
                  <p>{featuredEmptyMessage}</p>
                </div>
              ) : (
                <div className="featured-jobs-grid">
              {activeFeaturedList.map((job, index) => {
                const location =
                  job.location ||
                  [job.city, job.state, job.country].filter(Boolean).join(', ') ||
                  job.remoteType ||
                  'Flexible';

                const salaryRange =
                  job.salaryDisplay ||
                  job.salary_range ||
                  job.salary ||
                  job.stipend ||
                  (isInternshipView ? 'Stipend details shared during selection' : 'Competitive Compensation');

                const jobType =
                  job.jobType || job.job_type || job.type || (isInternshipView ? 'Internship' : 'Full-time');

                const experienceLevel =
                  job.experienceLevel ||
                  job.experience_level ||
                  job.experience_required ||
                  job.duration ||
                  job.eligibility ||
                  (isInternshipView ? 'Internship Opportunity' : 'All Levels');

                // Handle industry - it might be a string, array, or other type
                const getIndustryString = (value) => {
                  if (!value) return '';
                  if (typeof value === 'string') return value.trim();
                  if (Array.isArray(value)) return value.join(', ');
                  return String(value).trim();
                };

                const industryType =
                  getIndustryString(job.industry) ||
                  getIndustryString(job.domain) ||
                  getIndustryString(job.industryType) ||
                  'Not specified';

                const postedDate = job.postedAt ? new Date(job.postedAt) : null;
                const now = new Date();
                let postedText = 'Just now';
                if (postedDate) {
                  const diffTime = Math.abs(now - postedDate);
                  const diffDays = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
                  if (diffDays === 1) postedText = '1 day ago';
                  else if (diffDays < 7) postedText = `${diffDays} days ago`;
                  else if (diffDays < 30) {
                    const weeks = Math.floor(diffDays / 7);
                    postedText = `${weeks} week${weeks > 1 ? 's' : ''} ago`;
                  } else {
                    const months = Math.floor(diffDays / 30);
                    postedText = `${months} month${months > 1 ? 's' : ''} ago`;
                  }
                }

                const jobId = job.id || job._id;
                const companyName =
                  (job.companyName || job.company_name || job.company || '').trim() || 'Company';
                const jobTitle = (job.jobTitle || job.job_title || job.title || job.role || '').trim() || 'Job';
                const viewCount = typeof job.views === 'number' ? job.views : parseInt(job.views, 10) || 0;
                const applicationCount =
                  typeof job.applicationCount === 'number'
                    ? job.applicationCount
                    : parseInt(job.applicationCount, 10) || 0;
                const remoteLabel =
                  job.remoteOption || job.remote_option || job.workMode || job.work_mode
                    ? (job.remoteOption || job.remote_option || job.workMode || job.work_mode)
                    : job.remoteType || job.remote_type || null;

                const getCompanyInitial = (name) => {
                  if (!name) return 'A';
                  const str = String(name).trim();
                  return str ? str.charAt(0).toUpperCase() : 'A';
                };

                return (
                  <div
                    key={jobId || index}
                    className="featured-job-card"
                    role="article"
                    onClick={(e) => {
                      if (!e.target.closest('.fjc-apply-btn')) {
                        handleNavigateToJob(jobId, job.applyUrl);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.target.closest('.fjc-apply-btn')) {
                        e.preventDefault();
                        handleNavigateToJob(jobId, job.applyUrl);
                      }
                    }}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    aria-label={`View details for ${jobTitle}`}
                  >
                    {/* Row 1: Company + Job Title + Apply */}
                    <div className="fjc-job-row-1">
                      <div className="fjc-company">
                        <div className="fjc-logo" aria-hidden="true">
                          {getCompanyInitial(companyName)}
                        </div>
                        <div className="fjc-company-info">
                          <div className="fjc-company-name">{companyName}</div>
                          <div className="fjc-title">{jobTitle}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="fjc-apply-btn btn-custom-size"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleNavigateToJob(jobId, job.applyUrl);
                        }}
                      >
                        Apply
                      </button>
                    </div>

                    {/* Row 2: Tags + Salary | Location | Views */}
                    <div className="fjc-job-row-2">
                      <div className="fjc-tags" aria-label="Job tags">
                        {jobType && <span className="fjc-tag">{jobType}</span>}
                        {experienceLevel && <span className="fjc-tag">{experienceLevel}</span>}
                        {remoteLabel && <span className="fjc-tag">{remoteLabel}</span>}
                      </div>
                      <div className="fjc-details">
                        <span className="fjc-salary">{salaryRange}</span>
                        <span className="fjc-separator">|</span>
                        <span className="fjc-location">
                          <FontAwesomeIcon icon={faMapMarkerAlt} /> {location}
                        </span>
                        <span className="fjc-separator">|</span>
                        <span
                          className="fjc-stats"
                          title={`${applicationCount.toLocaleString()} applied · ${viewCount.toLocaleString()} views`}
                        >
                          <span className="fjc-applied">
                            <FontAwesomeIcon icon={faUserCheck} /> {applicationCount.toLocaleString()}
                          </span>
                          <span className="fjc-stats-separator">/</span>
                          <span className="fjc-views">
                            {viewCount.toLocaleString()} <FontAwesomeIcon icon={faEye} />
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              )}

              <div className="featured-view-all-wrap">
                <Link to={viewAllLink} className="btn btn-primary btn-large">
                  {viewAllLabel}
                </Link>
              </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories" id="categories">
        <h2 className="section-title section-title-live">Browse Jobs by Category</h2>
        <p className="section-subtitle">Explore opportunities across various industries and specializations</p>
        
        <div className="category-grid">
          <div className="category-card" onClick={() => handleCategoryClick('Technology & IT')}>
            <div className="category-icon">💻</div>
            <h3>Technology & IT</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Technology & IT'] ?? 0} open position${(categoryCounts['Technology & IT'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Healthcare')}>
            <div className="category-icon">🏥</div>
            <h3>Healthcare</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Healthcare'] ?? 0} open position${(categoryCounts['Healthcare'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Finance & Accounting')}>
            <div className="category-icon">📊</div>
            <h3>Finance & Accounting</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Finance & Accounting'] ?? 0} open position${(categoryCounts['Finance & Accounting'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Design & Creative')}>
            <div className="category-icon">🎨</div>
            <h3>Design & Creative</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Design & Creative'] ?? 0} open position${(categoryCounts['Design & Creative'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Marketing & Sales')}>
            <div className="category-icon">📱</div>
            <h3>Marketing & Sales</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Marketing & Sales'] ?? 0} open position${(categoryCounts['Marketing & Sales'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Education')}>
            <div className="category-icon">🎓</div>
            <h3>Education</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Education'] ?? 0} open position${(categoryCounts['Education'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Engineering & Manufacturing')}>
            <div className="category-icon">🏭</div>
            <h3>Engineering & Manufacturing</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Engineering & Manufacturing'] ?? 0} open position${(categoryCounts['Engineering & Manufacturing'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Legal Services')}>
            <div className="category-icon">⚖️</div>
            <h3>Legal Services</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Legal Services'] ?? 0} open position${(categoryCounts['Legal Services'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Hospitality & Tourism')}>
            <div className="category-icon">🏨</div>
            <h3>Hospitality & Tourism</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Hospitality & Tourism'] ?? 0} open position${(categoryCounts['Hospitality & Tourism'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Consumer Goods')}>
            <div className="category-icon">🛍️</div>
            <h3>Consumer Goods</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Consumer Goods'] ?? 0} open position${(categoryCounts['Consumer Goods'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Logistics & Supply Chain')}>
            <div className="category-icon">🚚</div>
            <h3>Logistics & Supply Chain</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Logistics & Supply Chain'] ?? 0} open position${(categoryCounts['Logistics & Supply Chain'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Retail & E-commerce')}>
            <div className="category-icon">🛒</div>
            <h3>Retail & E-commerce</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Retail & E-commerce'] ?? 0} open position${(categoryCounts['Retail & E-commerce'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Human Resources')}>
            <div className="category-icon">👥</div>
            <h3>Human Resources</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Human Resources'] ?? 0} open position${(categoryCounts['Human Resources'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Consulting & Professional Services')}>
            <div className="category-icon">📋</div>
            <h3>Consulting & Professional Services</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Consulting & Professional Services'] ?? 0} open position${(categoryCounts['Consulting & Professional Services'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
          <div className="category-card" onClick={() => handleCategoryClick('Construction & Real Estate')}>
            <div className="category-icon">🏗️</div>
            <h3>Construction & Real Estate</h3>
            <p>{categoryLoading ? 'Loading...' : `${categoryCounts['Construction & Real Estate'] ?? 0} open position${(categoryCounts['Construction & Real Estate'] ?? 0) !== 1 ? 's' : ''}`}</p>
          </div>
        </div>
      </section>

      {/* Various Roles Section */}
      <section className="roles-section">
        <div className="roles-container">
          <h2 className="section-title section-title-live">Explore Various Roles</h2>
          <p className="section-subtitle">Join thousands of professionals across different roles and industries</p>
          
          <div className="roles-grid">
            <div className="role-card">
              <h3 className="role-name">Job Seekers</h3>
              <p className="role-description">Find your dream job with AI-powered matching</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Employers</h3>
              <p className="role-description">Hire top talent and grow your business</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Recruiters</h3>
              <p className="role-description">Discover top talent faster with AI insights</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Mentors</h3>
              <p className="role-description">Guide and inspire the next generation</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Trainers</h3>
              <p className="role-description">Share your expertise and build skills</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Consultants</h3>
              <p className="role-description">Provide strategic guidance and expertise</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Volunteers</h3>
              <p className="role-description">Make a positive impact in your community</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Communities</h3>
              <p className="role-description">Network, learn and collaborate together</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">Universities</h3>
              <p className="role-description">Connect students with opportunities</p>
            </div>

            <div className="role-card">
              <h3 className="role-name">NGOs</h3>
              <p className="role-description">Drive social impact and create meaningful change</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" ref={statsSectionRef}>
        <div className="stats-container">
          {statCardConfig.map((card) => {
            // Use animated value if animation has started, otherwise show loading or 0
            let displayValue;
            if (statsLoading) {
              displayValue = null; // Will show "—" in formatStatValue
            } else if (hasAnimated) {
              displayValue = animatedStats[card.key];
            } else {
              displayValue = 0; // Start at 0 before animation triggers
            }
            return (
              <div className="stat-item" key={card.key}>
                <h3>{formatStatValue(displayValue, card.addPlus)}</h3>
                <p>{card.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent Activity Feed Section */}
      <section className="recent-activity-section">
        <div className="activity-container">
          <div className="activity-header">
            <h2 className="section-title section-title-live">
              <FontAwesomeIcon icon={faBell} className="activity-header-icon" />
              Live Activity
            </h2>
            <p className="section-subtitle">See what's happening right now on AksharJobs</p>
          </div>
          
          <div className="activity-feed">
            {displayedRecentActivity.map((activity, index) => (
              <div key={index} className="activity-item" data-activity-type={activity.type} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="activity-content">
                  <div className="activity-message">
                    {activity.type === 'job_found' ? (
                      <>
                        <span className="activity-name">Someone from {activity.location}</span>
                        <span className="activity-action">{activity.message}</span>
                      </>
                    ) : (
                      <>
                        <span className="activity-count">{activity.count}</span>
                        <span className="activity-action">{activity.message}</span>
                      </>
                    )}
                  </div>
                  <div className="activity-time">
                    <FontAwesomeIcon icon={faClock} className="time-icon" />
                    <span>{activity.time}</span>
                  </div>
                </div>
                <div className="activity-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(testimonialsLoading || testimonials.length > 0) && (
        <section className="testimonials-section">
          <div className="testimonials-container">
            <h2 className="section-title">What Our Users Say</h2>
            <p className="section-subtitle">Real stories from professionals who found success with AksharJobs</p>
            
            {testimonialsLoading ? (
              <div className="testimonial-loading">Loading success stories...</div>
            ) : (
              <div className="testimonial-slider">
                <div className="testimonial-content">
                  <div className="quote-icon">
                    <FontAwesomeIcon icon={faQuoteLeft} />
                  </div>
                  <blockquote className="testimonial-quote">
                    {activeTestimonial?.quote}
                  </blockquote>
                  <div className="testimonial-author">
                    {activeTestimonial?.photoUrl && (
                      <img
                        src={activeTestimonial.photoUrl}
                        alt={activeTestimonial.author}
                        className="author-photo"
                        loading="lazy"
                      />
                    )}
                    <div className="author-info">
                      <span className="author-name">{activeTestimonial?.author}</span>
                      <span className="author-role">{activeTestimonial?.role}</span>
                    </div>
                  </div>
                </div>
                
                <div className="testimonial-controls">
                  <button 
                    className="testimonial-btn prev-btn"
                    onClick={prevTestimonial}
                    aria-label="Previous testimonial"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  
                  <div className="testimonial-dots">
                    {testimonials.map((testimonial, index) => (
                      <button
                        key={testimonial.id || index}
                        className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                        onClick={() => setCurrentTestimonial(index)}
                        aria-label={`Go to testimonial ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  <button 
                    className="testimonial-btn next-btn"
                    onClick={nextTestimonial}
                    aria-label="Next testimonial"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-bg">
          <Aurora
            colorStops={["#ff8c42", "#ffa726", "#14b8a6"]}
            blend={0.5}
            amplitude={1.0}
            speed={0.5}
          />
        </div>
        <div className="cta-content">
          <h2>Ready to Take the Next Step?</h2>
          <p>
            Join thousands of professionals who found their perfect job through AksharJobs. Create your profile today and get discovered by top employers.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-section">
            <h3>For Job Seekers</h3>
            <ul>
              <li><Link to="/jobseeker-dashboard">Browse Jobs</Link></li>
              <li><Link to="/companies">Browse Employers</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>For Employers</h3>
            <ul>
              <li><Link to="/signup">Post a Job</Link></li>
              <li><Link to="/pricing">Pricing (Free)</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>About AksharJobs</h3>
            <ul>
              <li><Link to="/about-platform">About Platform</Link></li>
              <li><Link to="/contact-us">Contact</Link></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Support</h3>
            <ul>
              <li><Link to="/help-center">Help Center</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/cookie-policy">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Maxpro Infotech Limited. All rights reserved. | Connecting talent with opportunity.</p>
          <div className="footer-social-icons">
            <a href="https://www.linkedin.com/showcase/128903969/" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="AksharJobs on LinkedIn">
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <a href="https://whatsapp.com/channel/0029Vb7zasEIiRowJY3bQK0s" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Channel">
              <FontAwesomeIcon icon={faWhatsapp} />
            </a>
            <a href="https://twitter.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
              <FontAwesomeIcon icon={faTwitter} />
            </a>
            <a href="https://facebook.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <FontAwesomeIcon icon={faFacebook} />
            </a>
            <a href="https://instagram.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
          </div>
        </div>
      </footer>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <span>{toast.message}</span>
          <button onClick={closeToast}>&times;</button>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default HomePage;